import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { usePageState } from "@/hooks/usePageState";

export function NumBtn({ n, onClick, strong, testid }) {
  if (!n) return <span data-testid={testid} className="text-muted-foreground/40 tabular-nums">0</span>;
  return (
    <button data-testid={testid} onClick={onClick}
      className={cn("inline-flex min-w-[2rem] justify-center rounded px-1.5 py-0.5 tabular-nums transition-colors hover:underline",
        strong ? "font-black text-foreground hover:bg-primary/10" : "font-semibold text-primary hover:bg-primary/10")}>
      {n}
    </button>
  );
}

function periodBounds(period, cs, ce) {
  const day = 86400000;
  const now = new Date();
  const d0 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let s, e;
  if (period === "custom") {
    s = cs ? new Date(cs + "T00:00:00") : d0;
    e = ce ? new Date(new Date(ce + "T00:00:00").getTime() + day) : new Date(d0.getTime() + day);
  } else if (period === "yesterday") {
    s = new Date(d0.getTime() - day); e = d0;
  } else if (period === "7days") {
    s = new Date(d0.getTime() - 6 * day); e = new Date(d0.getTime() + day);
  } else if (period === "30days") {
    s = new Date(d0.getTime() - 29 * day); e = new Date(d0.getTime() + day);
  } else {
    s = d0; e = new Date(d0.getTime() + day);
  }
  return { s: s.toISOString(), e: e.toISOString() };
}

const PERIOD_OPTS = [
  { key: "today", label: "Oggi" },
  { key: "yesterday", label: "Ieri" },
  { key: "7days", label: "Ultimi 7 giorni" },
  { key: "30days", label: "Ultimi 30 giorni" },
  { key: "custom", label: "Personalizzato" },
];

export default function AssignmentMonitor({ navigate, isAdmin, stateKey = "dash" }) {
  const [rows, setRows] = useState(null);
  const [commercials, setCommercials] = useState([]);
  const [period, setPeriod] = usePageState(`am_${stateKey}_period`, "today");
  const [sel, setSel] = usePageState(`am_${stateKey}_sel`, "all");
  const [cs, setCs] = usePageState(`am_${stateKey}_cs`, "");
  const [ce, setCe] = usePageState(`am_${stateKey}_ce`, "");

  useEffect(() => {
    if (isAdmin) api.get("/users").then((r) => setCommercials((r.data || []).filter((u) => u.role === "commerciale"))).catch(() => {});
  }, [isAdmin]);

  const bounds = periodBounds(period, cs, ce);
  useEffect(() => {
    const params = { start: bounds.s, end: bounds.e };
    if (sel !== "all") params.assigned_to = sel;
    api.get("/dashboard/assignment-monitor", { params }).then((r) => setRows(r.data)).catch(() => setRows([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, sel, cs, ce]);

  const tot = (rows || []).reduce((a, r) => ({ assigned: a.assigned + r.assigned, worked: a.worked + r.worked, never_worked: a.never_worked + r.never_worked }), { assigned: 0, worked: 0, never_worked: 0 });
  const totPct = tot.assigned ? Math.round((tot.worked / tot.assigned) * 100) : 0;
  const pctCls = (p) => (p >= 70 ? "text-emerald-600 dark:text-emerald-400" : p >= 40 ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400");
  const go = (uid, state) => {
    let url = `/leads?assigned_to=${uid}&am_from=${encodeURIComponent(bounds.s)}&am_to=${encodeURIComponent(bounds.e)}`;
    if (state) url += `&am_state=${state}`;
    navigate(url);
  };

  return (
    <div className="rounded-lg border bg-card overflow-hidden" data-testid="assignment-monitor-section">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b flex-wrap">
        <div>
          <h2 className="font-heading font-bold text-lg">Monitoraggio lead assegnati</h2>
          <p className="text-xs text-muted-foreground">Per data di assegnazione (non di acquisizione) · clicca un numero per l'elenco</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {period === "custom" && (
            <>
              <input type="date" data-testid="am-custom-start" value={cs} onChange={(e) => setCs(e.target.value)} className="h-8 rounded-md border bg-background px-2 text-sm" />
              <input type="date" data-testid="am-custom-end" value={ce} onChange={(e) => setCe(e.target.value)} className="h-8 rounded-md border bg-background px-2 text-sm" />
            </>
          )}
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="h-8 w-40" data-testid="am-period-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{PERIOD_OPTS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
          {isAdmin && <Select value={sel} onValueChange={setSel}>
            <SelectTrigger className="h-8 w-52" data-testid="am-commercial-filter"><SelectValue placeholder="Tutti i commerciali" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i commerciali</SelectItem>
              {commercials.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>}
        </div>
      </div>
      {!rows ? <div className="p-6 text-center text-muted-foreground text-sm">…</div> :
        !isAdmin ? <IndividualTable row={rows[0]} go={go} /> :
        rows.length === 0 ? <div className="p-8 text-center text-muted-foreground text-sm" data-testid="am-empty">Nessun lead assegnato nel periodo selezionato.</div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b bg-muted/30">
                <th className="px-4 py-2.5 font-medium">Commerciale</th>
                <th className="px-3 py-2.5 font-medium text-right">Assegnati</th>
                <th className="px-3 py-2.5 font-medium text-right">Lavorati</th>
                <th className="px-3 py-2.5 font-medium text-right">Mai lavorati</th>
                <th className="px-3 py-2.5 font-medium text-right">% lavorati</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {rows.map((r) => (
                <tr key={r.user_id} data-testid={`am-row-${r.user_id}`} className="hover:bg-muted/40 transition-colors">
                  <td className="px-4 py-2.5 font-semibold whitespace-nowrap">
                    <span className="inline-flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: r.color || "#9ca3af" }} />
                      {r.name}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right"><NumBtn n={r.assigned} onClick={() => go(r.user_id, null)} testid={`am-assigned-${r.user_id}`} /></td>
                  <td className="px-3 py-2.5 text-right"><NumBtn n={r.worked} onClick={() => go(r.user_id, "worked")} testid={`am-worked-${r.user_id}`} /></td>
                  <td className="px-3 py-2.5 text-right"><NumBtn n={r.never_worked} strong onClick={() => go(r.user_id, "never_worked")} testid={`am-neverworked-${r.user_id}`} /></td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold">
                    <span className={pctCls(r.pct)}>{r.pct}%</span>
                  </td>
                </tr>
              ))}
            </tbody>
            {isAdmin && (
              <tfoot>
                <tr className="border-t-2 bg-muted/40 font-bold" data-testid="am-total-row">
                  <td className="px-4 py-2.5 uppercase text-xs tracking-wide">Totale</td>
                  <td className="px-3 py-2.5 text-right tabular-nums" data-testid="am-total-assigned">{tot.assigned}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums" data-testid="am-total-worked">{tot.worked}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums" data-testid="am-total-neverworked">{tot.never_worked}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums" data-testid="am-total-pct"><span className={pctCls(totPct)}>{totPct}%</span></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}

const eur = (v) => `€ ${Math.round(v || 0).toLocaleString("it-IT", { useGrouping: "always" })}`;

// Monitoraggio INDIVIDUALE del commerciale: 9 colonne, stessi criteri di Report/Performance
function IndividualTable({ row, go }) {
  const r = row || { user_id: "", assigned: 0, worked: 0, deals: {} };
  const d = r.deals || {};
  const cols = [
    { k: "assigned", label: "Assegnati", node: <NumBtn n={r.assigned} onClick={() => go(r.user_id, null)} testid="am-ind-assigned" /> },
    { k: "worked", label: "Lavorati", node: <NumBtn n={r.worked} onClick={() => go(r.user_id, "worked")} testid="am-ind-worked" /> },
    { k: "open", label: "Aperte", node: <NumBtn n={d.open_count || 0} onClick={() => go(r.user_id, "deal_open")} testid="am-ind-open" /> },
    { k: "open_v", label: "Valore aperto (€)", node: <button onClick={() => go(r.user_id, "deal_open")} className="tabular-nums hover:underline" data-testid="am-ind-open-value">{eur(d.open_value)}</button> },
    { k: "won", label: "Vinti", node: <NumBtn n={d.won_count || 0} onClick={() => go(r.user_id, "deal_won")} testid="am-ind-won" /> },
    { k: "won_v", label: "Valore vinti (€)", node: <button onClick={() => go(r.user_id, "deal_won")} className="tabular-nums hover:underline text-emerald-600 dark:text-emerald-400" data-testid="am-ind-won-value">{eur(d.won_value)}</button> },
    { k: "lost", label: "Persi", node: <NumBtn n={d.lost_count || 0} onClick={() => go(r.user_id, "deal_lost")} testid="am-ind-lost" /> },
    { k: "lost_v", label: "Valore perso (€)", node: <button onClick={() => go(r.user_id, "deal_lost")} className="tabular-nums hover:underline text-red-600 dark:text-red-400" data-testid="am-ind-lost-value">{eur(d.lost_value)}</button> },
    { k: "rate", label: "Tasso chiusura", node: <span className="tabular-nums font-semibold" data-testid="am-ind-close-rate">{d.close_rate || 0}%</span> },
  ];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="am-individual-table">
        <thead>
          <tr className="text-left text-xs text-muted-foreground border-b bg-muted/30">
            {cols.map((c) => <th key={c.k} className="px-3 py-2.5 font-medium text-right whitespace-nowrap">{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          <tr data-testid={`am-row-${r.user_id}`}>
            {cols.map((c) => <td key={c.k} className="px-3 py-3 text-right">{c.node}</td>)}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
